import React from "react";
import { Link } from "react-router-dom";
import { Avatar } from "../../../design/avatar";
import { getInitials } from "../../../utils/string";
import { Child, Case, calculateAge } from "@visitproof/types";
import {
  FolderIcon,
  CakeIcon,
  CalendarIcon,
} from "@heroicons/react/20/solid";
import moment from "moment";

interface ChildGridProps {
  children: Child[];
  cases: Case[];
}

const ChildGrid: React.FC<ChildGridProps> = ({ children, cases }) => {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {children.map((child) => (
        <ChildCard key={child.id} child={child} cases={cases} />
      ))}
    </div>
  );
};

interface ChildCardProps {
  child: Child;
  cases: Case[];
}

const ChildCard: React.FC<ChildCardProps> = ({ child, cases }) => {
  const caseItem = cases.find((c) => c.id === child.caseId);
  const fullName = `${child.firstName} ${child.lastName}`;

  // Calculate age from DOB
  const age = child.dob ? calculateAge(child.dob.toDate()) : null;

  return (
    <Link
      to={`/child/${child.id}`}
      className="group block p-5 bg-white border rounded-xl shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-300 dark:bg-zinc-900 dark:border-zinc-700 dark:hover:border-blue-600"
    >
      {/* Header with Avatar */}
      <div className="flex items-start gap-4 mb-4">
        <Avatar
          src={child.avatarUrl}
          className="size-14 ring-2 ring-zinc-100 dark:ring-zinc-800"
          initials={getInitials(fullName)}
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-zinc-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
            {fullName}
          </h3>
          {age !== null && (
            <div className="flex items-center gap-1.5 mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              <CakeIcon className="w-4 h-4" />
              <span>{age} years old</span>
            </div>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4">
        {child.dob && (
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <CalendarIcon className="w-4 h-4 text-zinc-400" />
            <span>DOB: {moment(child.dob.toDate()).format("MMM D, YYYY")}</span>
          </div>
        )}
      </div>

      {/* Case Info */}
      <div className="pt-3 border-t border-zinc-100 dark:border-zinc-800">
        <div className="flex items-center gap-2 text-sm">
          <FolderIcon className="w-4 h-4 text-zinc-400" />
          <span className="text-zinc-600 dark:text-zinc-400">Case:</span>
          <span className="font-medium text-zinc-900 dark:text-white truncate">
            {caseItem?.identifier || child.caseId || "Unassigned"}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ChildGrid;
